import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';

const API_URL = 'https://iia-backend-production.up.railway.app';

const PathView = () => {
  const { pathId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCourses();
  }, [pathId]);

  const fetchCourses = async () => {
    try {
      const res = await axios.get(
        `${API_URL}/api/instructional/paths/${pathId}/courses`
      );
      setCourses(res.data.courses);
    } catch (err) {
      console.error('Fetch courses error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <h1 style={styles.logo}>IIA</h1>
          <span style={styles.logoText}>Impact Influence Academy</span>
        </div>
        <div style={styles.headerRight}>
          <span style={styles.welcome}>Welcome, {user?.first_name}</span>
          <button 
            style={styles.returnBtn} 
            onClick={() => navigate('/instructional')}
          >
            ← Return to Instructional Center
          </button>
        </div>
      </div>

      <div style={styles.main}>
        <div style={styles.pageHeader}>
          <h2 style={styles.pageTitle}>🎓 Your Courses</h2>
          <p style={styles.pageSubtitle}>Select a course to begin</p>
        </div>

        {loading && (
          <div style={styles.centered}>
            <p style={styles.loadingText}>Loading courses...</p>
          </div>
        )}

        {!loading && courses.length === 0 && (
          <div style={styles.emptyState}>
            <p style={styles.emptyIcon}>📚</p>
            <h3 style={styles.emptyTitle}>No Courses Yet</h3>
            <p style={styles.emptyText}>
              No courses have been added to this path yet.
            </p>
          </div>
        )}

        {!loading && courses.length > 0 && (
          <div style={styles.coursesGrid}>
            {courses.map((course) => (
              <div
                key={course.id}
                style={styles.courseCard}
                onClick={() => navigate(`/instructional/course/${course.id}`)}
              >
                <div style={styles.courseThumbnail}>
                  {course.thumbnail_url ? (
                    <img
                      src={course.thumbnail_url}
                      alt={course.title}
                      style={styles.thumbnailImg}
                    />
                  ) : (
                    <div style={styles.thumbnailPlaceholder}>
                      <span style={styles.thumbnailIcon}>📚</span>
                    </div>
                  )}
                </div>
                <div style={styles.courseInfo}>
                  <h3 style={styles.courseTitle}>{course.title}</h3>
                  <p style={styles.courseDescription}>
                    {course.description}
                  </p>
                  <button style={styles.enterBtn}>Enter Course →</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#1A2744',
    color: '#ffffff',
  },
  header: {
    backgroundColor: '#0f1a33',
    borderBottom: '1px solid #C49A28',
    padding: '16px 32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  logo: {
    color: '#C49A28',
    fontSize: '24px',
    fontWeight: '800',
    margin: '0',
    letterSpacing: '2px',
  },
  logoText: {
    color: '#ffffff80',
    fontSize: '14px',
    letterSpacing: '1px',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  welcome: {
    color: '#ffffff',
    fontSize: '14px',
  },
  returnBtn: {
    backgroundColor: 'transparent',
    border: '1px solid #C49A28',
    color: '#C49A28',
    padding: '8px 16px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
  },
  main: {
    maxWidth: '1100px',
    margin: '0 auto',
    padding: '32px',
  },
  pageHeader: {
    marginBottom: '32px',
  },
  pageTitle: {
    color: '#C49A28',
    fontSize: '28px',
    fontWeight: '700',
    margin: '0 0 4px 0',
  },
  pageSubtitle: {
    color: '#ffffff60',
    fontSize: '14px',
    margin: '0',
  },
  centered: {
    display: 'flex',
    justifyContent: 'center',
    padding: '60px',
  },
  loadingText: {
    color: '#ffffff60',
    fontSize: '16px',
  },
  emptyState: {
    textAlign: 'center',
    padding: '80px 40px',
    backgroundColor: '#0f1a33',
    borderRadius: '12px',
    border: '1px solid #ffffff15',
  },
  emptyIcon: {
    fontSize: '48px',
    margin: '0 0 16px 0',
  },
  emptyTitle: {
    color: '#ffffff',
    fontSize: '20px',
    fontWeight: '600',
    margin: '0 0 12px 0',
  },
  emptyText: {
    color: '#ffffff60',
    fontSize: '14px',
    maxWidth: '400px',
    margin: '0 auto',
    lineHeight: '1.6',
  },
  coursesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: '24px',
  },
  courseCard: {
    backgroundColor: '#0f1a33',
    border: '1px solid #ffffff15',
    borderRadius: '12px',
    overflow: 'hidden',
    cursor: 'pointer',
  },
  courseThumbnail: {
    height: '180px',
    backgroundColor: '#1A2744',
    overflow: 'hidden',
  },
  thumbnailImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  thumbnailPlaceholder: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#1A374D',
  },
  thumbnailIcon: {
    fontSize: '48px',
  },
  courseInfo: {
    padding: '20px',
  },
  courseTitle: {
    color: '#ffffff',
    fontSize: '18px',
    fontWeight: '600',
    margin: '0 0 8px 0',
  },
  courseDescription: {
    color: '#ffffff80',
    fontSize: '14px',
    margin: '0 0 16px 0',
    lineHeight: '1.5',
  },
  enterBtn: {
    backgroundColor: '#C49A28',
    color: '#1A2744',
    border: 'none',
    borderRadius: '6px',
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: '700',
    cursor: 'pointer',
  },
};

export default PathView;